import { searchInput } from '@/utils/format-search-string'
import { validateDate } from '@/utils/validators'
import { finnishDateStringToIsoDateString } from '@/utils/date-mapper'

/**
 * MONITOR_SEARCH_INPUTS contains all the search inputs for Status Monitor
 * the key is the name of the input provided to the backend search api
 * the value is an object that contains the following:
 * 1- validator: a function that takes a string and returns a boolean indicating whether the string is valid or not
 * 2- mapper: a function that takes a string and returns another string
 * 3- items: an array of strings that will be used to match the input with the key
 */
function createMonitorSearchInputs () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return {
    access_point_phone_number: searchInput(['access point phone number', 'accesspointphonenumber', 'apn', messages.getTableColumnAccessPointPhoneNumber()]),
    address_code: searchInput(['address code', 'addresscode', 'ac', messages.getTableColumnAddressCode()]),
    carrier: searchInput(['carrier', 'car', messages.getTableColumnCarrier()]),
    created: searchInput(['created', 'cr', messages.getTableColumnCreated()], validateDate, finnishDateStringToIsoDateString),
    delivery_address: searchInput(['delivery address', 'deliveryaddress', 'da', messages.getTableColumnDeliveryAddress()]),
    delivery_order_doc_number: searchInput(['delivery order', 'deliveryorder', 'do', messages.getTableColumnDeliveryOrder()]),
    delivery_status: searchInput(['delivery status', 'deliverystatus', 'ds', messages.getTableColumnDeliveryStatus()]),
    equipment_code: searchInput(['equipment code', 'equipmentcode', 'ec', messages.getTableColumnEquipmentCode()]),
    equipment_description: searchInput(['equipment description', 'equipmentdescription', 'eqd', messages.getTableColumnEquipmentDescription()]),
    material_code: searchInput(['material code', 'materialcode', 'mc', messages.getTableColumnMaterialCode()]),
    material_description: searchInput(['material description', 'materialdescription', 'md', messages.getTableColumnMaterialDescription()]),
    planner_group: searchInput(['planner group', 'plannergroup', 'pg', messages.getTableColumnPlannerGroup()]),
    plant: searchInput(['plant', 'pl', messages.getTableColumnPlant()]),
    purchase_order_doc_number: searchInput(['purchase order', 'purchaseorder', 'po', messages.getTableColumnPurchaseOrder()]),
    reservation_doc_number: searchInput(['reservation', 'res', messages.getTableColumnReservation()]),
    sales_order_doc_number: searchInput(['sales order', 'salesorder', 'so', messages.getTableColumnSalesOrder()]),
    service_order: searchInput(['service order', 'serviceorder', 'so', messages.getTableColumnServiceOrder()]),
    ship_to_party: searchInput(['ship to party', 'shiptoparty', messages.getTableColumnShipToParty()]),
    storage_location: searchInput(['storage location', 'storagelocation', 'sloc', messages.getTableColumnStorageLocation()]),
    tracking_number: searchInput(['tracking number', 'trackingnumber', 'tn', messages.getTableColumnTrackingNumber()]),
    transport_details: searchInput(['transport details', 'transportdetails', 'td', messages.getTableColumnTransportDetails()]),
    work_center: searchInput(['work center', 'workcenter', 'wc', messages.getTableColumnWorkCenter()]),
    priority: searchInput(['priority', 'pr', messages.getFieldPriority()]),
    query: searchInput(['search', 'q', 'query'])
  }
}

let _monitorSearchInputs = null
export const MONITOR_SEARCH_INPUTS = new Proxy({}, {
  get (target, prop) {
    if (!_monitorSearchInputs) {
      _monitorSearchInputs = createMonitorSearchInputs()
    }
    return _monitorSearchInputs[prop]
  },
  ownKeys (target) {
    if (!_monitorSearchInputs) {
      _monitorSearchInputs = createMonitorSearchInputs()
    }
    return Reflect.ownKeys(_monitorSearchInputs)
  },
  has (target, prop) {
    if (!_monitorSearchInputs) {
      _monitorSearchInputs = createMonitorSearchInputs()
    }
    return prop in _monitorSearchInputs
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_monitorSearchInputs) {
      _monitorSearchInputs = createMonitorSearchInputs()
    }
    return Reflect.getOwnPropertyDescriptor(_monitorSearchInputs, prop)
  }
})

/**
 * MONITOR_SEARCH_SUGGESTIONS contains all the search input suggestions
 * that will be displayed to the user in the search autocomplete dropdown
 */
function createMonitorSearchSuggestions () {
  // Import messages lazily to avoid circular dependency
  const { messages } = require('@/utils/strings')
  return [
    messages.getTableColumnAccessPointPhoneNumber(),
    messages.getTableColumnAddressCode(),
    messages.getTableColumnCarrier(),
    messages.getTableColumnCreated(),
    messages.getTableColumnDeliveryAddress(),
    messages.getTableColumnDeliveryOrder(),
    messages.getTableColumnDeliveryStatus(),
    messages.getTableColumnEquipmentCode(),
    messages.getTableColumnEquipmentDescription(),
    messages.getTableColumnMaterialCode(),
    messages.getTableColumnMaterialDescription(),
    messages.getTableColumnPlannerGroup(),
    messages.getTableColumnPlant(),
    messages.getFieldPriority(),
    messages.getTableColumnPurchaseOrder(),
    messages.getTableColumnReservation(),
    messages.getTableColumnSalesOrder(),
    messages.getTableColumnServiceOrder(),
    messages.getTableColumnShipToParty(),
    messages.getTableColumnStorageLocation(),
    messages.getTableColumnTrackingNumber(),
    messages.getTableColumnTransportDetails(),
    messages.getTableColumnWorkCenter()
  ].sort()
}

let _monitorSearchSuggestions = null
export const MONITOR_SEARCH_SUGGESTIONS = new Proxy([], {
  get (target, prop) {
    if (!_monitorSearchSuggestions) {
      _monitorSearchSuggestions = createMonitorSearchSuggestions()
    }
    return _monitorSearchSuggestions[prop]
  },
  ownKeys (target) {
    if (!_monitorSearchSuggestions) {
      _monitorSearchSuggestions = createMonitorSearchSuggestions()
    }
    return Reflect.ownKeys(_monitorSearchSuggestions)
  },
  has (target, prop) {
    if (!_monitorSearchSuggestions) {
      _monitorSearchSuggestions = createMonitorSearchSuggestions()
    }
    return prop in _monitorSearchSuggestions
  },
  getOwnPropertyDescriptor (target, prop) {
    if (!_monitorSearchSuggestions) {
      _monitorSearchSuggestions = createMonitorSearchSuggestions()
    }
    return Reflect.getOwnPropertyDescriptor(_monitorSearchSuggestions, prop)
  }
})
